import { appManager, AppState } from '../manager';
import current from 'immer';
import {
  addElementToIndexedArray,
  arrayToIndexedObject,
  makeAPIProp,
} from 'services/indexedObject';

import * as types from '../types';
import { CREATING, TRANSACTION_TYPES } from '../constants';
import { authManager, utils } from '../../index';
import Cookies from 'js-cookie';
import {
  AnalysisType,
  AssetClassOptions,
  AssetOverlayType,
  BenchmarkComponent,
  BiasesType,
  BiasReportType,
  RegimeOverlayType,
  MarketRegimeReportType,
  ConcentrationType,
  InsightsReport,
  LLMGroupResponseType,
  LeaderboardScoresType,
  LeaderboardWeightsType,
  LLMResponseType,
  LoanRateOfferOptions,
  MarginOptions,
  MarketHoursType,
  MetricLimits,
  PlayerAllocationsByInvestmentProfile,
  PlayerAnalysisType,
  PlayerTurnoverType,
  RiskScatterType,
  PnLDistributionAnalysisType,
  Preset,
  PromptKind,
  PromptType,
  TargetAllocationsByInvestmentProfile,
  // RateOfferOptions,
} from '../types';
import { message, notification } from 'antd';
import errors from '../../../views/AdminDashboard/Errors';
import notificationCenter from '../../../views/NotificationCenter';
import {
  formatAFSBondIssueDetails,
  formatAFSBondIssueTitle,
} from './afsBondIssueNotification';
import { mergeAFSDV01History } from '../players/afsDv01History';
import { mergeValuation } from '../players/valuation';
import {
  currentPlayerData,
  hydratePlayer,
  updateOrQueuePlayer,
} from '../players/pendingUpdates';

// NOTE TO DEVELOPER:
//  PAGINATED SESSIONS ARE ONLY USED FOR PERFORMANCE REASONS IN THE ADMIN TAB
//  ALL player and / or relevant data is to be stored in the INDEXED SESSIONS
//  Duplicating data in redux is not a good idea
// TODO: When fetching paginated sessions, get the data and store it in the indexed sessions, then store the ids in the
//  paginated state. Therefore, removing and duplicated data.
//  Consider caching pages and creating cache clearing for specific actions like: DELETE, UPDATE, CREATE OR
//  create a system where paginated api provides only ids and then the frontend requests the ids which are not stored
//  in state.

const removeOtherLLMResponsesForPair = (
  state: AppState,
  response: LLMResponseType,
) => {
  Object.values(state.llm_responses.items).forEach(existingResponse => {
    if (
      existingResponse.id !== response.id &&
      existingResponse.prompt === response.prompt &&
      existingResponse.player === response.player
    ) {
      delete state.llm_responses.items[existingResponse.id];
    }
  });
};

const removeOtherLLMGroupResponsesForIdentity = (
  state: AppState,
  response: LLMGroupResponseType,
) => {
  Object.values(state.llm_group_responses.items).forEach(existingResponse => {
    if (
      existingResponse.id !== response.id &&
      existingResponse.prompt === response.prompt &&
      existingResponse.session === response.session &&
      existingResponse.group_report_name === response.group_report_name
    ) {
      delete state.llm_group_responses.items[existingResponse.id];
    }
  });
};

const setPromptStoreWaiting = (
  state: AppState,
  promptType: PromptKind,
  waiting: boolean,
) => {
  if (promptType === 'group') {
    state.group_prompts.waiting = waiting;
    return;
  }

  state.prompts.waiting = waiting;
};

const upsertPromptByType = (state: AppState, prompt: PromptType) => {
  delete state.prompts.items[prompt.id];
  delete state.group_prompts.items[prompt.id];

  if (prompt.type === 'group') {
    state.group_prompts.items[prompt.id] = prompt;
    return;
  }

  state.prompts.items[prompt.id] = prompt;
};

const updatePromptTextInStores = (
  state: AppState,
  promptId: number,
  text: string,
) => {
  if (state.prompts.items[promptId]) {
    state.prompts.items[promptId].text = text;
  }
  if (state.group_prompts.items[promptId]) {
    state.group_prompts.items[promptId].text = text;
  }
};
const normalizeApiError = (error: unknown): string => {
  if (error == null) {
    return 'Request failed.';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (Array.isArray(error)) {
    return error.map(normalizeApiError).join('\n');
  }

  if (typeof error === 'object') {
    const entries = Object.entries(error as Record<string, unknown>);
    if (entries.length === 0) {
      return JSON.stringify(error);
    }

    return entries
      .map(([key, value]) => {
        const message = normalizeApiError(value);
        if (key === 'detail' || key === 'non_field_errors') {
          return message;
        }
        return `${key}: ${message}`;
      })
      .join('\n');
  }

  return String(error);
};

export const createSession = appManager.createApi<
  types.CreateSessionPayload,
  types.CreateSessionResponse,
  AppState
>('CREATE_SESSION', {
  path: '/sessions',
  method: 'POST',
  startReducer(state) {
    state.create_session_request = {
      waiting: true,
      succeeded: false,
      failed: false,
    };
  },
  successReducer(state, result) {
    state.create_session_request = {
      waiting: false,
      succeeded: true,
      failed: false,
    };

    // The new session itself arrives on the klass socket.
    // Reset transient replay defaults so the next plain create opens fresh.
    state.replay_session_defaults = {
      item: undefined,
      source_session_id: undefined,
      waiting: false,
      fetched: false,
    };

    if (result.replay_copy_warnings?.length) {
      notification.warning({
        message: 'Replay copied with warnings',
        description: result.replay_copy_warnings.join('\n'),
        duration: 0,
      });
    }
  },
  failReducer(state, error) {
    state.create_session_request = {
      waiting: false,
      succeeded: false,
      failed: true,
    };

    notification.error({
      message: 'Create session failed',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const deleteSession = appManager.createApi<
  { id: number },
  { id: number },
  AppState
>('DELETE_SESSION', {
  path: '/sessions/:id/delete',
  method: 'POST',
  successReducer(state, result) {
    // Indexed sessions
    delete state.sessions.items[result.id];

    // Admin list sessions
    const index = state.paginated_sessions.items.findIndex(
      s => s.id === result.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items.splice(index, 1);
      if (state.paginated_sessions.count) {
        state.paginated_sessions.count -= 1;
      }
    }
  },
});

export const fetchSessionAssets = appManager.createApi<
  { id: number },
  // unknown,
  types.UnderlyingAsset[],
  AppState
>('FETCH_SESSION_ASSETS', {
  path: '/sessions/:id/fetch_session_assets',
  method: 'GET',
  successReducer(state, result) {
    state.underlying_assets.items = arrayToIndexedObject(result);
    state.underlying_assets.fetched = true;
  },
});

export const fetchSessionReplayDefaults = appManager.createApi<
  { id: number },
  types.ReplaySessionDefaults,
  AppState
>('FETCH_SESSION_REPLAY_DEFAULTS', {
  path: '/sessions/:id/replay_defaults',
  method: 'GET',
  startReducer(state, payload) {
    state.replay_session_defaults = {
      item: undefined,
      source_session_id: Number(payload.id),
      waiting: true,
      fetched: false,
    };
  },
  successReducer(state, result, payload) {
    state.replay_session_defaults = {
      item: result,
      source_session_id: Number(payload.id),
      waiting: false,
      fetched: true,
    };
  },
  failReducer(state, error, payload) {
    state.replay_session_defaults = {
      item: undefined,
      source_session_id: Number(payload.id),
      waiting: false,
      fetched: false,
    };
  },
});

export const clearSessionReplayDefaults = appManager.createLocalEvent(
  'clear_session_replay_defaults',
  state => {
    state.replay_session_defaults = {
      item: undefined,
      source_session_id: undefined,
      waiting: false,
      fetched: false,
    };
  },
);

// Clears the global `active_session` singleton. Used by the blueprint authoring
// view on unmount so the template session id doesn't linger as the global
// singleton and get picked up by a later id-less settings dispatch (the
// `session_id` selector-merge at manager.ts feeds every id-less dispatch).
export const resetActiveSession = appManager.createLocalEvent(
  'reset_active_session',
  state => {
    state.active_session = 0;
    state.retrieving_session = undefined;
  },
);

appManager.createSocketListener<types.Session, AppState>(
  'session_created',
  (state, result) => {
    addElementToIndexedArray(state.sessions.items, result);
  },
);

appManager.createSocketListener<types.Session, AppState>(
  'session_updated',
  (state, result) => {
    // Indexed sessions — MERGE, don't replace. `analysis`, `concentrations`,
    // `pnl_distributions`, `asset_overlay` (and flags like *_fetched) are
    // client-only fields stored on the session item by separate fetches; they
    // are NOT in this socket payload, so a full replace wipes them (e.g. the
    // Analysis Studio data vanishing after a historical period pass emits
    // session_updated). Backend fields in `result` still win.
    const existing = state.sessions.items[result.id];
    state.sessions.items[result.id] = existing
      ? { ...existing, ...result }
      : result;
    // if (result.limit_metric === null) {
    //   state.sessions.items[result.id].limit_metric = undefined;
    // } // this turns back to null
    // Admin list sessions
    const index = state.paginated_sessions.items.findIndex(
      s => s.id === result.id,
    );
    if (index !== -1) {
      const session = state.paginated_sessions.items[index];
      state.paginated_sessions.items[index] = { ...session, ...result };
    }
  },
);

export const fetchSessions = appManager.createApi<
  unknown,
  types.Session[],
  AppState
>('FETCH_SESSIONS', {
  path: '/sessions',
  method: 'GET',
  startReducer(state) {
    state.sessions.waiting = true;
  },
  successReducer(state, result) {
    // Merge per-session instead of replacing the whole dict. Separate fetches
    // stash CLIENT-ONLY fields on a session item — `analysis`, `concentrations`,
    // `pnl_distributions`, `turnover`, `market_regimes`, `risk_scatter`,
    // `asset_overlay` (the Analysis Studio data) — that are NOT in this payload.
    // This bootstrap call (GET /sessions, dispatched on entering a session)
    // resolves AFTER the studio's own fetches, so a full replace was wiping them
    // (the per-player Analysis Studio rendering empty until a tab cycle re-ran
    // those fetches). Backend fields still win; sessions no longer returned are
    // dropped (same as the previous full replace). Mirrors the `session_updated`
    // socket merge above.
    const incoming = arrayToIndexedObject(result);
    Object.keys(incoming).forEach(id => {
      const existing = state.sessions.items[id];
      if (existing) incoming[id] = { ...existing, ...incoming[id] };
    });
    state.sessions.items = incoming;
    state.sessions.waiting = false;
  },
  failReducer(state) {
    state.sessions.waiting = false;
  },
});

export const fetchHistoricalStep = appManager.createApi<
  unknown,
  {
    sleep_time: number;
  },
  AppState
>('FETCH_HISTORICAL_STEP', {
  path: '/sessions/get_historical_period_length',
  method: 'GET',

  successReducer(state, result) {
    // console.log(result);
    // no longer used, switched to session.historical_sleep_time
    state.historical_period_length = result.sleep_time;
  },
});

export const updateHistoricalStep = appManager.createApi<
  {
    session_id: number;
    sleep_time: number;
    admin: boolean;
  },
  {
    sleep_time: number;
    remaining_time: string;
  },
  AppState
>('UPDATE_HISTORICAL_STEP', {
  path: '/sessions/:session_id/update_historical_period_length',
  method: 'POST',

  successReducer(state, result, payload) {
    // no longer used, switched to session.historical_sleep_time
    // state.historical_period_length = result.sleep_time;

    console.log(result);
    state.sessions.items[payload.session_id].historical_sleep_time =
      result.sleep_time;
    state.sessions.items[payload.session_id].remaining_session_time =
      result.remaining_time;
  },
});

export type HistoricalNewsDeliverySettings = {
  session_id: number;
  historical_news_delivery_enabled: boolean;
  historical_news_push_interval_seconds: number;
  historical_news_quiet_seconds: number;
};

export const updateHistoricalNewsDelivery = appManager.createApi<
  HistoricalNewsDeliverySettings,
  HistoricalNewsDeliverySettings & { effective_from: 'next_period' },
  AppState
>('UPDATE_HISTORICAL_NEWS_DELIVERY', {
  path: '/sessions/:session_id/update_historical_news_delivery',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) return;
    session.historical_news_delivery_enabled =
      result.historical_news_delivery_enabled;
    session.historical_news_push_interval_seconds =
      result.historical_news_push_interval_seconds;
    session.historical_news_quiet_seconds =
      result.historical_news_quiet_seconds;
  },
});

appManager.createSocketListener<HistoricalNewsDeliverySettings, AppState>(
  'historical_news_delivery_updated',
  (state, result) => {
    const session = state.sessions.items[result.session_id];
    if (!session) return;
    session.historical_news_delivery_enabled =
      result.historical_news_delivery_enabled;
    session.historical_news_push_interval_seconds =
      result.historical_news_push_interval_seconds;
    session.historical_news_quiet_seconds =
      result.historical_news_quiet_seconds;
  },
);

export const updateFrequency = appManager.createApi<
  {
    session_id: number;
    frequency: number;
  },
  {
    session_id: number;
    frequency: number;
  },
  AppState
>('UPDATE_FREQUENCY', {
  path: '/sessions/:session_id/update_frequency',
  method: 'POST',

  successReducer(state, result, payload) {
    // Update the initiating client directly from the response so the value +
    // day/days label refresh immediately (other clients get it via the socket).
    const session = state.sessions.items[payload.session_id];
    if (session) {
      session.frequency = result.frequency;
    }
  },
});

appManager.createSocketListener<
  {
    session_id: number;
    frequency: number;
  },
  AppState
>('frequency_updated', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (session) {
    session.frequency = result.frequency;
  }
});

export const updateHardSoftLimits = appManager.createApi<
  {
    session_id: number;
    hard_limits: boolean;
  },
  unknown,
  AppState
>('UPDATE_HARD_SOFT_LIMITS', {
  path: '/sessions/:session_id/update_hard_soft_limits',
  method: 'POST',

  successReducer(state, result, payload) {
    // response recieved via socket message
  },
});

appManager.createSocketListener<
  {
    hard_limits: boolean;
  },
  AppState
>('hard_soft_limits_updated', (state, result) => {
  const session = state.sessions.items[state.active_session];
  session.hard_limits = result.hard_limits;
});

export const updateMRR = appManager.createApi<
  {
    session_id: number;
    mrr: number;
  },
  unknown,
  AppState
>('UPDATE_MRR', {
  path: '/sessions/:session_id/update_mrr',
  method: 'POST',

  successReducer(state, result, payload) {
    // response sent to whole class via ws
  },
});

export const updateConcurrentDeals = appManager.createApi<
  {
    session_id: number;
    concurrent_deals: boolean;
  },
  {
    concurrent_deals: boolean;
  },
  AppState
>('UPDATE_CONCURRENT_DEALS', {
  path: '/sessions/:session_id/update_concurrent_deals',
  method: 'POST',

  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].concurrent_deals =
      result.concurrent_deals;
  },
});

export const updateStopIfDiffs = appManager.createApi<
  {
    session_id: number;
    stop_if_differences: boolean;
  },
  {
    stop_if_differences: boolean;
  },
  AppState
>('UPDATE_STOP_IF_DIFFS', {
  path: '/sessions/:session_id/update_stop_if_differences',
  method: 'POST',

  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].stop_if_differences =
      result.stop_if_differences;
  },
});

export const updateAutoTradeOnlyInRepCcy = appManager.createApi<
  {
    session_id: number;
    auto_trade_only_in_reporting_ccy: boolean;
  },
  {
    auto_trade_only_in_reporting_ccy: boolean;
  },
  AppState
>('UPDATE_AUTO_TRADE_ONLY_IN_REPORTING_CCY', {
  path: '/sessions/:session_id/update_auto_trade_only_in_reporting_ccy',
  method: 'POST',

  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].auto_trade_only_in_reporting_ccy =
      result.auto_trade_only_in_reporting_ccy;
  },
});

export const updateAutoODA = appManager.createApi<
  {
    session_id: number;
    auto_oda: boolean;
  },
  {
    auto_oda: boolean;
  },
  AppState
>('UPDATE_AUTO_ODA', {
  path: '/sessions/:session_id/update_auto_oda',
  method: 'POST',

  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].auto_oda = result.auto_oda;
  },
});

appManager.createSocketListener<
  {
    sleep_time: number;
    remaining_time: string;
  },
  AppState
>('historical_step_updated', (state, result) => {
  const session = state.sessions.items[state.active_session];
  // state.historical_period_length = result.sleep_time; // no longer used, switched to session.historical_sleep_time
  session.historical_sleep_time = result.sleep_time;
  session.remaining_session_time = result.remaining_time;
});

appManager.createSocketListener<number, AppState>(
  'mrr_updated',
  (state, result) => {
    const session = state.sessions.items[state.active_session];
    session.mrr = result;
  },
);

export const fetchPaginatedSessions = appManager.createApi<
  { page_number?: number; page_size?: number; params?: string },
  {
    count: number;
    page_number: number;
    page_size: number;
    results: types.Session[];
  },
  AppState
>('FETCH_PAGINATED_SESSIONS', {
  path: '/sessions/paginated_list',
  method: 'GET',
  startReducer(state) {
    state.paginated_sessions.waiting = true;
  },
  successReducer(state, result) {
    state.paginated_sessions.items = result.results;
    state.paginated_sessions.count = result.count;
    state.paginated_sessions.page_size = result.page_size;
    state.paginated_sessions.page_number = result.page_number;
    state.paginated_sessions.revision = (state.paginated_sessions.revision || 0) + 1;
    state.paginated_sessions.waiting = false;
  },
  failReducer(state) {
    state.paginated_sessions.waiting = false;
  },
});

export const updateSessionBillable = appManager.createApi<
  { session_id: number; billable: boolean },
  { session_id: number; billable: boolean },
  AppState
>('UPDATE_SESSION_BILLABLE', {
  path: '/sessions/:session_id/update_billable',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (session) {
      session.billable = result.billable;
    }

    const paginatedSession = state.paginated_sessions.items.find(
      candidate => candidate.id === result.session_id,
    );
    if (paginatedSession) {
      paginatedSession.billable = result.billable;
    }
  },
  failReducer(_state, error) {
    notification.error({
      message: 'Could not update billable status',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const fetchPlayers = appManager.createApi<
  { session_id: number },
  types.Player[],
  AppState
>('FETCH_PLAYERS', {
  path: '/sessions/:session_id/fetch_players',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[payload.session_id].players_fetched = true;
  },
  successReducer(state, result) {
    result.forEach(player => hydratePlayer(state, player));
  },
});

export const fetchPassedNews = appManager.createApi<
  { session_id: number },
  {
    data: types.News[];
    last_data: types.News[];
    staggered_delivery_active?: boolean;
    session_id?: number;
    delivery_order?: number[];
    current_period_news_ids?: number[];
    delivery_period_elapsed_days?: number;
  },
  AppState
>('FETCH_PASSED_NEWS', {
  path: '/sessions/:session_id/fetch_passed_news',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[payload.session_id].news_fetched = false;
    state.sessions.items[payload.session_id].fetching_news = true;
  },
  successReducer(state, result, payload) {
    if (result.staggered_delivery_active) {
      // Remove stories a pre-plan legacy fetch may already have cached. The
      // server returns only delivery-visible news while a plan is active.
      state.news.items = arrayToIndexedObject(result.data);
      state.news.historical_delivery = {
        session_id: result.session_id ?? payload.session_id,
        period_elapsed_days:
          result.delivery_period_elapsed_days ??
          state.sessions.items[payload.session_id].elapsed_days,
        delivery_order: result.delivery_order || [],
        current_period_news_ids:
          result.current_period_news_ids ||
          result.last_data.map(news => news.id),
      };
    } else {
      state.news.items = {
        ...state.news.items,
        ...arrayToIndexedObject(result.data),
      };
      state.news.historical_delivery = undefined;
    }
    state.news.last = arrayToIndexedObject(result.last_data);
    state.sessions.items[payload.session_id].last_news_source = 'fetch';
    state.sessions.items[payload.session_id].news_fetched = true;
    state.sessions.items[payload.session_id].fetching_news = false;
  },
});

export const fetchAllocations = appManager.createApi<
  { session_id: number },
  { player_id: number; allocations: types.TacticalAssetAllocation[] }[],
  AppState
>('FETCH_ALLOCATIONS', {
  path: '/sessions/:session_id/fetch_tactical_asset_allocations',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    session.tactical_allocations_fetching = true;
  },
  successReducer(state, result, payload) {
    result.forEach(({ player_id, allocations }) => {
      updateOrQueuePlayer(state, player_id, {
        tactical_asset_allocations: allocations,
      });
    });
    const session = state.sessions.items[payload.session_id];
    session.tactical_allocations_fetching = false;
    session.tactical_allocations_fetched = true;
  },
});

export const fetchAssetGroupAllocations = appManager.createApi<
  { session_id: number },
  types.AssetGroupAllocation[],
  AppState
>('FETCH_ASSET_GROUP_ALLOCATIONS', {
  path: '/sessions/:session_id/fetch_asset_group_allocations',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    session.fetching_asset_group_allocations = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    session.asset_group_allocations = result;
    session.fetching_asset_group_allocations = false;
    session.fetched_asset_group_allocations = true;
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    session.fetching_asset_group_allocations = false;
  },
});

export const fetchMetrics = appManager.createApi<
  { session_id: number },
  {
    id: number;
    metrics: types.Metrics[];
    player_history: types.PlayerHistory[];
  }[],
  AppState
>('FETCH_METRICS', {
  path: '/sessions/:session_id/fetch_metrics',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[payload.session_id].metrics_fetching = true;
    state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    result.forEach(({ id, metrics, player_history }) => {
      updateOrQueuePlayer(state, id, { metrics, player_history });
    });
    state.sessions.items[payload.session_id].metrics_fetching = false;
  },
});

// Trainer hydration for the predetermined-intervals display fills (mirrors fetchMetrics; shape
// [{id, display_series}]). Trainee needs no separate fetch -- display_series rides
// retrieveSession's result.player. REPLACES the array (idempotent, unlike the socket append);
// the merge with any fills that raced in via display_series_filled is safe because the endpoint
// returns the complete set.
export const fetchDisplaySeries = appManager.createApi<
  { session_id: number },
  {
    id: number;
    display_series: types.DisplaySeriesPoint[];
  }[],
  AppState
>('FETCH_DISPLAY_SERIES', {
  path: '/sessions/:session_id/display_series',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[payload.session_id].display_series_fetching = true;
    state.sessions.items[payload.session_id].display_series_fetched = true;
  },
  successReducer(state, result, payload) {
    result.forEach(({ id, display_series }) => {
      updateOrQueuePlayer(state, id, { display_series });
    });
    state.sessions.items[payload.session_id].display_series_fetching = false;
  },
});

export const playPauseSession = appManager.createApi<
  {
    id: number;
    current_status: 0 | 1 | 2 | 3 | 4 | 5;
    reload?: boolean;
  },
  { id: number; status: types.Session['status'] },
  AppState
>('PLAY_PAUSE_SESSION', {
  path: '/sessions/:id/play_pause/',
  method: 'POST',
  successReducer(state, result, payload) {
    // Admin list sessions (Admin sessions)
    const index = state.paginated_sessions?.items.findIndex(
      s => s.id === result.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].status = result.status;
      return;
    }

    // Indexed session (Trainer dashboard && Trainer session).
    // When a reload is requested (e.g. finishing creation), the full page
    // reload rebuilds the store from the backend with the authoritative status,
    // so we must NOT flip status in-place first: doing so re-renders the
    // Overview into its "playing" form (mounting PlayersEvolutionGraph) against
    // player/metrics data that only arrives on the reload, which throws
    // mid-render. Just reload — the fresh load carries the correct status.
    if (payload.reload) {
      window.location.reload();
      return;
    }
    state.sessions.items[result.id].status = result.status;
  },
  failReducer(state, error, payload) {
    // Admin list sessions (Admin sessions)
    const index = state.paginated_sessions?.items.findIndex(
      s => s.id === payload.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].status = payload.current_status;
      return;
    }

    // Indexed session (Trainer dashboard && Trainer session)
    state.sessions.items[payload.id].status = payload.current_status;

    notification.error({
      message:
        payload.current_status === CREATING
          ? 'Could not start session'
          : 'Could not update session status',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const freezeSession = appManager.createApi<
  { id: number },
  { id: number; status: types.Session['status'] },
  AppState
>('FREEZE_SESSION', {
  path: '/sessions/:id/freeze_session/',
  method: 'POST',
  successReducer(state, result, payload) {
    // Admin list sessions (Admin sessions)
    const index = state.paginated_sessions?.items.findIndex(
      s => s.id === result.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].status = result.status;
      return;
    }
    // Indexed session (Trainer dashboard && Trainer session)
    state.sessions.items[result.id].status = result.status;

    // payload.reload && window.location.reload();
  },
});

export const switchHTMPermission = appManager.createApi<
  { id: number },
  { id: number; allow_htm: boolean },
  AppState
>('SWITCH_HTM', {
  path: '/sessions/:id/switch_htm_permission',
  method: 'POST',
  successReducer(state, result, payload) {
    state.sessions.items[result.id].allow_htm = result.allow_htm;
  },
});

export const finishSession = appManager.createApi<
  { id: number; reload?: boolean },
  { id: number; status: types.Session['status'] },
  AppState
>('FINISH_SESSION', {
  path: '/sessions/:id/finish',
  method: 'POST',
  successReducer(state, result, payload) {
    // Admin list sessions
    const index = state.paginated_sessions?.items.findIndex(
      s => s.id === result.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].status = result.status;
      return;
    }

    // Indexed session
    state.sessions.items[result.id].status = result.status;

    payload.reload && window.location.reload();
  },
});

export const fetchSession = appManager.createApi<
  { id: number },
  types.Session,
  AppState
>('FETCH_SESSION', {
  path: '/sessions/:id/fetch_session',
  method: 'GET',
  successReducer(state, result) {
    state.sessions.items[result.id] = result;
  },
});

export const retrieveSession = appManager.createApi<
  // fetches both retired and not retired assets so old sessions can still be accessed
  { id: number },
  any,
  AppState
>('RETRIEVE_SESSION', {
  path: '/sessions/:id', // def the_actual_retrieve in backend
  method: 'GET',
  startReducer(state, payload) {
    state.retrieving_session = Number(payload.id);
  },
  failReducer(state, _error, payload) {
    if (state.retrieving_session === Number(payload.id))
      state.retrieving_session = undefined;
  },
  successReducer(state, result) {
    const active_session_id = result.active_session;
    if (state.retrieving_session === active_session_id)
      state.retrieving_session = undefined;
    state.active_session = active_session_id;
    const liveRevision = state.sessions.items[active_session_id]?.live_bond_revision;
    state.sessions.items[active_session_id] = result.session;
    if (liveRevision) state.sessions.items[active_session_id].live_bond_revision = liveRevision;
    // if (result.session.limit_metric === null) {
    //   state.sessions.items[active_session_id].limit_metric = undefined;
    // } // this turns back to null
    state.persons = makeAPIProp(result.persons);
    if (result.index_funds) state.index_funds = makeAPIProp(result.index_funds);
    if (result.player) {
      // Socket reconciliation can beat the HTTP snapshot. Keep the newest
      // summary, including when entering a different room or an empty store.
      const previous = state.player.item.id === result.player.id
        ? state.player.item : undefined;
      const pending = state.players.pending_updates?.[result.player.id];
      const player = mergeValuation(pending, mergeValuation(previous, result.player));
      if (pending) delete state.players.pending_updates![result.player.id];
      // state.player = { item: result.player, waiting: false };
      state.player = {
        item: {
          ...state.player?.item,
          ...player,
          financial_instruments: arrayToIndexedObject(
            result.player.financial_instruments,
          ),
        },
        waiting: false,
      };
      // state.player.item.financial_instruments = arrayToIndexedObject(
      //   result.player.financial_instruments
      // );
    }
    if (result.players) {
      result.players.forEach((player: types.Player) =>
        hydratePlayer(state, player),
      );
      // Keep the existing replace-roster semantics without dropping early data.
      state.players = {
        ...state.players,
        ...makeAPIProp(
          result.players.map(
            (player: types.Player) => state.players.items[player.id],
          ),
        ),
      };
    }
    state.underlying_assets = makeAPIProp(
      result.underlying_assets.map((el: any) => {
        return {
          ...el,
          market_history: {},
        };
      }),
    );
    state.currencies = makeAPIProp(result.currencies);
    const reportingCurrency =
      state.currencies.items[result.session.reporting_currency];
    if (!reportingCurrency) {
      return console.error('Reporting currency not found!');
    }
    state.currencies.reportingCurrency = reportingCurrency.id;
  },
});

export const retrievePlayer = appManager.createApi<
  { player_id: number; session_id?: number },
  { player: any; orders: types.Order[] },
  AppState
>('RETRIEVE_PLAYER', {
  path: '/sessions/:session_id/retrieve_player',
  method: 'GET',
  startReducer(state, payload) {
    if (!state.players.items[payload.player_id]) return;
    state.players.items[payload.player_id].fetched_data = true;
  },
  successReducer(state, result) {
    const { player, orders } = result;
    updateOrQueuePlayer(state, player.id, {
      ...player,
      financial_instruments: arrayToIndexedObject(player.financial_instruments),
      fetched_success: true,
      fetched_data: true,
    });

    const newOrders = arrayToIndexedObject(orders);
    state.orders.items = { ...state.orders.items, ...newOrders };
  },
});

export const updateSessionBenchmarkComposition = appManager.createApi<
  {
    portfolio_composition?: types.BenchmarkPortfolioComposition[];
    composition?: types.BenchmarkComposition[];
    constant?: number;
  },
  {
    portfolio_composition: types.BenchmarkPortfolioComposition[];
    composition: types.BenchmarkComposition[];
    constant: number;
  },
  AppState
>('UPDATE_BENCHMARK_COMPOSITION', {
  path: '/sessions/:session_id/update_benchmark_composition',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    session.benchmark_compositions = result.composition;
    session.benchmark_portfolio_compositions = result.portfolio_composition;
    session.benchmark_constant = result.constant;
  },
});

export const clearBenchmarkPreview = appManager.createLocalEvent(
  'clear_benchmark_preview',
  state => {
    // Fired as a component-unmount cleanup. active_session may already be reset (e.g. leaving the
    // blueprint authoring view resets it to 0), so guard against a missing session.
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.benchmark_preview = undefined;
  },
);

export const clearInstrumentPreview = appManager.createLocalEvent(
  'clear_instrument_preview',
  state => {
    // Same unmount-cleanup guard as clearBenchmarkPreview above.
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.instrument_preview = undefined;
  },
);

export const requestInstrumentPricePreview = appManager.createApi<
  {
    asset_id: number;
  },
  {
    prices: {
      asset_name: string;
      bid: number[];
      ask: number[];
      dates?: string[];
    };
  },
  AppState
>('PREVIEW_INSTRUMENT_PRICE', {
  path: '/sessions/:session_id/preview_instrument_price',
  method: 'POST',
  successReducer(state, result) {
    // Response can land after the view unmounted and reset active_session (blueprint authoring) — guard.
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.instrument_preview = result.prices;
  },
  failReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.instrument_preview = { bid: [0], ask: [0], asset_name: 'error' };
  },
});

export const requestBenchmarkPreview = appManager.createApi<
  {
    portfolio_composition?: types.BenchmarkPortfolioComposition[];
    composition?: types.BenchmarkComposition[];
    constant?: number;
  },
  {
    benchmark_returns: number[];
    components_dict: { [componentName: string]: number[] };
    errors: string[];
    dates?: string[];
  },
  AppState
>('PREVIEW_BENCHMARK', {
  path: '/sessions/:session_id/preview_index_benchmark',
  method: 'POST',
  startReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    session.bechmark_preview_new = {
      composite: [],
      components: {},
      errors: [],
      dates: [],
    };
  },
  successReducer(state, result) {
    console.log(result);
    // Response can land after the view unmounted and reset active_session (blueprint authoring) — guard.
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.benchmark_preview = result.benchmark_returns;
    session.bechmark_preview_new.composite = result.benchmark_returns;
    session.bechmark_preview_new.dates = result.dates || [];
    const components: { [key: string]: BenchmarkComponent } = {};

    for (const [componentName, values] of Object.entries(
      result.components_dict,
    )) {
      components[componentName] = {
        name: componentName,
        values, // array of numbers
      };
    }

    session.bechmark_preview_new.components = components;
    if (result.errors.length > 0) {
      notification.warning({
        message: 'Missing benchmark prices',
        description: result.errors,
        duration: 0,
      });
    }
  },
  failReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    if (!session) {
      return;
    }
    session.benchmark_preview = [0];
    // A failed preview otherwise leaves bechmark_preview_new at the empty startReducer placeholder,
    // which the preview reads as "loading" forever. Flag it errored so the chart shows an error state.
    session.bechmark_preview_new = {
      composite: [],
      components: {},
      errors: [
        'Could not compute the benchmark return for the current selection.',
      ],
      dates: [],
    };
  },
});

export const fetchEligibleBenchmarkOptions = appManager.createApi<
  { session_id: number },
  types.EligibleBenchmarkOptions,
  AppState
>('FETCH_ELIGIBLE_BENCHMARK_OPTIONS', {
  path: '/sessions/:session_id/eligible_benchmark_options',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.eligible_benchmark_options_waiting = true;
    session.benchmark_coverage_summary_waiting = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.eligible_benchmark_options = result;
    session.eligible_benchmark_options_waiting = false;
  },
  failReducer(state, _error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.eligible_benchmark_options_waiting = false;
  },
});

export const fetchBenchmarkSuggestion = appManager.createApi<
  { session_id: number },
  types.BenchmarkSuggestion | null,
  AppState
>('FETCH_BENCHMARK_SUGGESTION', {
  path: '/sessions/:session_id/suggested_benchmark',
  method: 'GET',
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_suggestion = result;
    session.benchmark_suggestion_waiting = false;
  },
  failReducer(state, _error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_suggestion_waiting = false;
  },
});

export const generateBenchmarkSuggestion = appManager.createApi<
  { session_id: number; provider?: string },
  { queued: number; session_id: number },
  AppState
>('GENERATE_BENCHMARK_SUGGESTION', {
  path: '/sessions/:session_id/generate_benchmark_suggestion',
  method: 'POST',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_suggestion_waiting = true;
  },
  successReducer() {
    notification.info({
      message: 'Benchmark suggestion queued. It will appear here when ready.',
    });
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (session) {
      session.benchmark_suggestion_waiting = false;
    }
    notification.error({
      message: 'Benchmark suggestion failed to start',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const recomputeSessionBenchmarkCoverage = appManager.createApi<
  { session_id: number },
  {
    queued: boolean;
    session_id: number;
  },
  AppState
>('RECOMPUTE_SESSION_BENCHMARK_COVERAGE', {
  path: '/sessions/:session_id/recompute_benchmark_coverage',
  method: 'POST',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.eligible_benchmark_options_waiting = true;
    session.benchmark_coverage_summary_waiting = true;
  },
  successReducer(state, result, payload) {
    notification.info({
      message: 'Benchmark coverage recompute started in background.',
    });
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (session) {
      session.eligible_benchmark_options_waiting = false;
      session.benchmark_coverage_summary_waiting = false;
      session.benchmark_coverage_diagnostics_waiting = false;
    }
    notification.error({
      message: 'Benchmark coverage recompute failed',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const fetchBenchmarkCoverageSummary = appManager.createApi<
  { session_id: number },
  types.BenchmarkCoverageSummary,
  AppState
>('FETCH_BENCHMARK_COVERAGE_SUMMARY', {
  path: '/sessions/:session_id/benchmark_coverage_summary',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_summary_waiting = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_summary = result;
    session.benchmark_coverage_summary_waiting = false;
  },
  failReducer(state, _error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_summary_waiting = false;
  },
});

export const fetchBenchmarkCoverageDiagnostics = appManager.createApi<
  { session_id: number },
  types.BenchmarkCoverageDiagnostics,
  AppState
>('FETCH_BENCHMARK_COVERAGE_DIAGNOSTICS', {
  path: '/sessions/:session_id/benchmark_coverage_diagnostics',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_diagnostics_waiting = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_diagnostics = result;
    session.benchmark_coverage_diagnostics_waiting = false;
  },
  failReducer(state, _error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_coverage_diagnostics_waiting = false;
  },
});

// Admin "fill missing date" (index funds + equities). Preview = read the carry-forward source +
// accepted gap for one (kind, asset, date); apply = create the price point + audit, returning the
// recomputed diagnostic item which we splice back in place (no diagnostics refetch needed).
// IndexFund and Equity have separate id spaces, so the cache key and splice are keyed by `kind`.
export const fetchBenchmarkFillPreview = appManager.createApi<
  {
    session_id: number;
    kind: types.BenchmarkFillKind;
    id: number;
    date: string;
  },
  types.BenchmarkFillPreview,
  AppState
>('FETCH_BENCHMARK_FILL_PREVIEW', {
  path: '/sessions/:session_id/benchmark_fill_preview',
  method: 'GET',
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    if (!session.benchmark_fill_previews) {
      session.benchmark_fill_previews = {};
    }
    session.benchmark_fill_previews[
      `${payload.kind}:${payload.id}:${payload.date}`
    ] = result;
  },
});

export const applyBenchmarkFill = appManager.createApi<
  {
    session_id: number;
    kind: types.BenchmarkFillKind;
    id: number;
    date: string;
  },
  types.BenchmarkCoverageDiagnosticItem,
  AppState
>('APPLY_BENCHMARK_FILL', {
  path: '/sessions/:session_id/benchmark_fill',
  method: 'POST',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_fill_waiting = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_fill_waiting = false;
    const diagnostics = session.benchmark_coverage_diagnostics;
    if (diagnostics) {
      const list =
        payload.kind === 'equity'
          ? diagnostics.equities
          : diagnostics.index_funds;
      const idx = list.findIndex(item => item.id === result.id);
      if (idx !== -1) {
        list[idx] = result;
      }
    }
    // Drop the consumed preview so the next oldest missing date re-fetches fresh.
    if (session.benchmark_fill_previews) {
      delete session.benchmark_fill_previews[
        `${payload.kind}:${payload.id}:${payload.date}`
      ];
    }
  },
  failReducer(state, _error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_fill_waiting = false;
  },
});

export const updateAllowIndividualBenchmark = appManager.createApi<
  { allow: boolean },
  { allow: boolean; comps: types.BenchmarkComposition[] },
  AppState
>('UPDATE_ALLOW_INDIVIDUAL_BENCHMARK', {
  path: '/sessions/:session_id/update_allow_individual_benchmark',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    session.allow_individual_benchmark = result.allow;
    // console.log(result.comps);
    session.benchmark_compositions = result.comps;
  },
});

appManager.createSocketListener<{ allow: boolean }, AppState>(
  'update_allow_individual_benchmark',
  (state, result) => {
    state.sessions.items[state.active_session].allow_individual_benchmark =
      result.allow;
  },
);

// Blueprint authoring (admin channel): a dates/frequency change triggered a coverage recompute — show
// the Options-library spinner until 'eligible_benchmark_options_updated' clears it below.
appManager.createSocketListener<{ session_id: number }, AppState>(
  'benchmark_coverage_recompute_started',
  (state, result) => {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.eligible_benchmark_options_waiting = true;
  },
);

appManager.createSocketListener<
  {
    session_id: number;
    index_funds: types.IndexFund[];
    equities: types.BenchmarkEquityOption[];
  },
  AppState
>('eligible_benchmark_options_updated', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.eligible_benchmark_options = {
    index_funds: result.index_funds,
    equities: result.equities,
  };
  session.eligible_benchmark_options_waiting = false;
});

appManager.createSocketListener<types.BenchmarkSuggestion, AppState>(
  'benchmark_suggestion_generated',
  (state, result) => {
    const session = state.sessions.items[result.session];
    if (!session) {
      return;
    }
    const previous = session.benchmark_suggestion;
    const sameSuggestion = previous?.id === result.id;
    session.benchmark_suggestion = {
      ...result,
      explanation:
        result.explanation ??
        (sameSuggestion ? previous?.explanation : undefined),
      provider:
        result.provider ?? (sameSuggestion ? previous?.provider : undefined),
      model_name:
        result.model_name ??
        (sameSuggestion ? previous?.model_name : undefined),
    };
    session.benchmark_suggestion_waiting = false;
  },
);

appManager.createSocketListener<
  { id: number; session_id: number; is_stale?: boolean; stale?: boolean },
  AppState
>('benchmark_suggestion_stale', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  if (session.benchmark_suggestion) {
    session.benchmark_suggestion.is_stale = true;
    session.benchmark_suggestion.stale = true;
  }
});

appManager.createSocketListener<
  { id: number; session_id: number; status: number },
  AppState
>('benchmark_suggestion_canceled', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.benchmark_suggestion_waiting = false;
  session.benchmark_suggestion = {
    ...(session.benchmark_suggestion || {
      id: result.id,
      session: result.session_id,
      components: [],
    }),
    id: result.id,
    session: result.session_id,
    status: result.status,
  };
});

appManager.createSocketListener<
  { id: number; session_id: number; status: number },
  AppState
>('benchmark_suggestion_error', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.benchmark_suggestion_waiting = false;
  session.benchmark_suggestion = {
    ...(session.benchmark_suggestion || {
      id: result.id,
      session: result.session_id,
      components: [],
    }),
    id: result.id,
    session: result.session_id,
    status: result.status,
  };
  notification.error({ message: 'Benchmark suggestion failed.' });
});

// Blueprint authoring (admin channel): a dates/frequency change made saved benchmark components
// ineligible for the new window, so the backend reset the saved composition. Mirror the reset on the FE
// (empty the basket + clear the now-invalid preview) and sticky-notify the admin which components went.
appManager.createSocketListener<
  { session_id: number; dropped: { name: string; ric: string }[] },
  AppState
>('benchmark_composition_reset', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.benchmark_compositions = [];
  session.benchmark_preview = undefined;
  session.bechmark_preview_new = {
    composite: [],
    components: {},
    errors: [],
    dates: [],
  };
  const names = (result.dropped || [])
    .map(d => `${d.name} (${d.ric})`)
    .join(', ');
  notification.warning({
    message: 'Benchmark reset',
    description: `These benchmark components are no longer available for the selected period and were removed: ${names}. Your saved benchmark was cleared — pick new eligible options.`,
    duration: 0,
  });
});

appManager.createSocketListener<{ allow: boolean }, AppState>(
  'update_allow_htm',
  (state, result) => {
    if (result.allow === true) {
      notification.info({
        message: 'HTM re-balancing now allowed.',
      });
    }
    state.sessions.items[state.active_session].allow_htm = result.allow;
  },
);

appManager.createSocketListener<{ delay_minutes_left: number }, AppState>(
  'update_delay',
  (state, result) => {
    // console.log('decreased delay');
    state.sessions.items[state.active_session].delay_minutes_left =
      result.delay_minutes_left;
  },
);

appManager.createSocketListener<types.SessionAssetGroupAllocation[], AppState>(
  'updated_asset_group_allocations',
  (state, result) => {
    const session = state.sessions.items[state.active_session];
    session.asset_group_allocations = result;
  },
);

appManager.createSocketListener<types.AssetClassOptions[], AppState>(
  'updated_asset_class_options',
  (state, result) => {
    const session = state.sessions.items[state.active_session];
    session.asset_class_options = result;
  },
);

appManager.createSocketListener<types.PassTime, AppState>(
  'passed_time',
  (state, result) => {
    try {
      state.sessions.items[state.active_session].elapsed_days =
        result.elapsed_days;
      state.sessions.items[state.active_session].remaining_session_time =
        result.remaining_session_time;
      state.sessions.items[state.active_session].remaining_simulated_days =
        result.remaining_simulated_days;
      state.sessions.items[state.active_session].scenario_date =
        result.scenario_date;
      state.sessions.items[state.active_session].periods_to_next_roll =
        result.periods_to_next_roll;
      state.sessions.items[state.active_session].afs_bond_market_replacements =
        result.afs_bond_market_replacements;
    } catch (error) {
      console.log(error);
    }
  },
);

appManager.createSocketListener<types.NewAFSBondIssuesPayload, AppState>(
  'new_afs_bond_issues',
  (state, result) => {
    if (result.session_id !== state.active_session) return;

    result.bonds.forEach(bond => {
      notification.info({
        key: `afs-bond-issue:${result.session_id}:${bond.underlying_asset_id}:${result.scenario_date}`,
        message: formatAFSBondIssueTitle(bond),
        description: formatAFSBondIssueDetails(bond),
      });
    });
  },
);

appManager.createSocketListener<
  {
    fins: {
      id: number;
      htm_afs_days_to_coupon: number;
      htm_afs_years_to_maturity: number;
      htm_afs_dv01: number;
      htm_afs_duration: number;
    }[];
  },
  // unknown,
  // UpdateFins[],
  AppState
>('update_htm_instruments', (state, result) => {
  const instruments = state.player.item.financial_instruments;
  result.fins.forEach(
    ({
      id,
      htm_afs_days_to_coupon,
      htm_afs_years_to_maturity,
      htm_afs_dv01,
      htm_afs_duration,
    }) => {
      instruments[id].htm_afs_days_to_coupon = htm_afs_days_to_coupon;
      instruments[id].htm_afs_years_to_maturity = htm_afs_years_to_maturity;
      instruments[id].htm_afs_dv01 = htm_afs_dv01;
      instruments[id].htm_afs_duration = htm_afs_duration;
    },
  );
});

export const updateTradingIntervals = appManager.createApi<
  { intervals: types.TradingInterval[] },
  types.TradingInterval[],
  AppState
>('UPDATE_TRADING_INTERVALS', {
  path: '/sessions/:session_id/update_trading_windows',
  method: 'POST',
  successReducer(state, result) {
    state.sessions.items[state.active_session].trading_intervals = result;
  },
});

export const updateAssetClassOptions = appManager.createApi<
  { asset_class_options: { [key: number]: AssetClassOptions } },
  // types.AssetClassOptions,
  types.AssetClassOptions[],
  AppState
>('UPDATE_ASSET_CLASS_OPTIONS', {
  path: '/sessions/:session_id/update_asset_class_options',
  method: 'POST',
  successReducer(state, result) {
    // response received on socket at updated_asset_class_options
  },
});

export const updateLeaderboardWeights = appManager.createApi<
  { leaderboard_weights: { [key: number]: LeaderboardWeightsType } },
  // types.AssetClassOptions,
  types.LeaderboardWeightsType[],
  AppState
>('UPDATE_LEADERBOARD_WEIGHTS', {
  path: '/sessions/:session_id/update_leaderboard_weights',
  method: 'POST',
  successReducer(state, result) {
    // response received on socket at updated_asset_class_options
    state.sessions.items[state.active_session].leaderboard_weights = result;
  },
});

export const updateLeverageSettings = appManager.createApi<
  {
    margins: { [key: string]: MarginOptions };
    futures_maintenance_threshold?: number;
    futures_rollover_periods?: number;
    futures_rollover_fee_pct?: number;
  },
  {
    fx_leverage: number;
    bond_leverage: number;
    equity_leverage: number;
    etf_equities_leverage: number;
    etf_commodities_leverage: number;
    futures_leverage: number;
    futures_maintenance_threshold: number;
    futures_rollover_periods: number;
    futures_rollover_fee_pct: number;
    periods_to_next_roll: number;
  },
  AppState
>('UPDATE_LEVERAGE_SETTINGS', {
  path: '/sessions/:session_id/update_leverage_settings',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    session.fx_leverage = result.fx_leverage;
    session.bond_leverage = result.bond_leverage;
    session.equity_leverage = result.equity_leverage;
    session.etf_equities_leverage = result.etf_equities_leverage;
    session.etf_commodities_leverage = result.etf_commodities_leverage;
    session.futures_leverage = result.futures_leverage;
    session.futures_maintenance_threshold =
      result.futures_maintenance_threshold;
    session.futures_rollover_periods = result.futures_rollover_periods;
    session.futures_rollover_fee_pct = result.futures_rollover_fee_pct;
    session.periods_to_next_roll = result.periods_to_next_roll;
  },
});

appManager.createSocketListener<
  { which_leverage: string; new_percentage: number },
  AppState
>('update_leverage_options', (state, result) => {
  const session = state.sessions.items[state.active_session];
  if (!session) {
    return;
  }
  if (result.which_leverage === 'FX Forwards') {
    session.fx_leverage = result.new_percentage;
  } else {
    session.bond_leverage = result.new_percentage;
  }
});

export const fetchMacroBackground = appManager.createApi<
  { session_id: number },
  { session_id: number; macro_background: string },
  AppState
>('FETCH_MACRO_BACKGROUND', {
  path: '/sessions/:session_id/fetch_macro_background',
  method: 'GET',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];

    session.macro_background = result.macro_background;
    session.fetched_macro_background = true;
  },
});

export const updateMacroBackground = appManager.createApi<
  { session_id: number; macro_background: string },
  { session_id: number; macro_background: string },
  AppState
>('UPDATE_MACRO_BACKGROUND', {
  path: '/sessions/:session_id/update_macro_background',
  method: 'POST',
  successReducer(state, _result, payload) {
    // The endpoint returns 204 (no body), so persist the SUBMITTED value into
    // the store. Otherwise the store stays stale: the rail's "Briefing set"
    // flag never flips and the editor (which re-seeds its text from the store
    // on remount) loses the text until a page reload re-fetches it.
    const session = state.sessions.items[payload.session_id];
    session.macro_background = payload.macro_background;
    session.fetched_macro_background = true;
  },
});

export const updateSessionUnderlyingAssets = appManager.createApi<
  { session_id: number; underlying_assets: number[] },
  {
    errors: string[];
    assets: number[];
    sector_alpha_settings: types.SectorAlphaSettings;
  },
  AppState
>('UPDATE_UNDERLYING_ASSETS', {
  path: '/sessions/:session_id/update_underlying_assets',
  method: 'POST',
  successReducer(state, result, payload) {
    console.log(result.assets);
    const session = state.sessions.items[payload.session_id];
    session.underlying_assets = result.assets;
    session.sector_alpha_settings = result.sector_alpha_settings;
    console.log(result.errors);
    if (result.errors.length > 0) {
      notification.warning({
        message: 'Some assets could not be added: ' + result.errors,
        duration: 0,
      });
    }
  },
});

export type NotionalLimitSettingsResponse = {
  live_synthetic_bonds_enabled?: boolean;
  live_bond_families?: types.Session["live_bond_families"];
  session_id: number;
  instrument_limit_mode: 0 | 1;
  underlying_assets: number[];
  notional_limits: types.SessionUnderlyingAssetNotionalLimit[];
  notional_limit_eligibility: types.NotionalLimitEligibility[];
  reporting_currency: string;
  sector_alpha_settings?: types.SectorAlphaSettings;
  configuration_errors: types.NotionalLimitConfigurationErrors;
  launch_ready: boolean;
};

const applyNotionalLimitSettingsResponse = (
  state: AppState,
  result: NotionalLimitSettingsResponse,
) => {
  const session = state.sessions.items[result.session_id];
  if (!session) return;
  if (result.live_synthetic_bonds_enabled) {
    session.live_synthetic_bonds_enabled = true;
    session.live_bond_families = result.live_bond_families;
  }
  session.instrument_limit_mode = result.instrument_limit_mode;
  session.underlying_assets = result.underlying_assets;
  session.underlying_asset_notional_limits = result.notional_limits;
  const eligibilityByAsset = new Map(
    (session.notional_limit_eligibility ?? []).map(row => [
      row.underlying_asset,
      row,
    ]),
  );
  result.notional_limit_eligibility.forEach(row =>
    eligibilityByAsset.set(row.underlying_asset, row),
  );
  session.notional_limit_eligibility = [...eligibilityByAsset.values()];
  session.notional_limit_configuration_errors = result.configuration_errors;
  session.notional_limit_launch_ready = result.launch_ready;
  session.notional_limit_settings_hydrated = true;
  if (result.sector_alpha_settings) {
    session.sector_alpha_settings = result.sector_alpha_settings;
  }
};

/** Apply a response produced by the serialized authoring-draft API client. */
export const receiveNotionalLimitSettings = appManager.createLocalEvent(
  'RECEIVE_NOTIONAL_LIMIT_SETTINGS',
  applyNotionalLimitSettingsResponse,
);

export const updateNotionalLimitSettings = appManager.createApi<
  {
    session_id: number;
    instrument_limit_mode: 0 | 1;
    underlying_assets: number[];
    notional_limits: types.SessionUnderlyingAssetNotionalLimit[];
  },
  NotionalLimitSettingsResponse,
  AppState
>('UPDATE_NOTIONAL_LIMIT_SETTINGS', {
  path: '/sessions/:session_id/update_notional_limit_settings',
  method: 'POST',
  successReducer(state, result) {
    applyNotionalLimitSettingsResponse(state, result);
  },
});

appManager.createSocketListener<NotionalLimitSettingsResponse, AppState>(
  'notional_limit_settings_updated',
  (state, result) => {
    applyNotionalLimitSettingsResponse(state, result);
  },
);

export const fetchNotionalLimitEligibility = appManager.createApi<
  { session_id: number },
  {
    session_id: number;
    instrument_limit_mode: 0 | 1;
    underlying_assets: number[];
    notional_limits: types.SessionUnderlyingAssetNotionalLimit[];
    eligibility: types.NotionalLimitEligibility[];
    configuration_errors: types.NotionalLimitConfigurationErrors;
    launch_ready: boolean;
  },
  AppState
>('FETCH_NOTIONAL_LIMIT_ELIGIBILITY', {
  path: '/sessions/:session_id/notional_limit_eligibility',
  method: 'GET',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) return;
    session.instrument_limit_mode = result.instrument_limit_mode;
    session.underlying_assets = result.underlying_assets;
    session.underlying_asset_notional_limits = result.notional_limits;
    session.notional_limit_eligibility = result.eligibility;
    session.notional_limit_configuration_errors = result.configuration_errors;
    session.notional_limit_launch_ready = result.launch_ready;
    session.notional_limit_settings_hydrated = true;
    session.notional_limit_settings_hydration_version =
      (session.notional_limit_settings_hydration_version ?? 0) + 1;
  },
});

export const togglePerSectorAlpha = appManager.createApi<
  { session_id: number; per_sector_alpha: boolean },
  {
    session_id: number;
    per_sector_alpha: boolean;
    sector_alpha_settings: types.SectorAlphaSettings;
  },
  AppState
>('TOGGLE_PER_SECTOR_ALPHA', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_per_sector_alpha',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.per_sector_alpha = result.per_sector_alpha;
    session.sector_alpha_settings = result.sector_alpha_settings;
  },
  failReducer(state, error) {
    notification.error({
      message: 'Could not update per-sector alpha setting',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const updateSessionSectorBenchmark = appManager.createApi<
  {
    session_id: number;
    sector_name: string;
    benchmark_asset_id: number | null;
  },
  {
    session_id: number;
    sector_alpha_settings: types.SectorAlphaSettings;
  },
  AppState
>('UPDATE_SESSION_SECTOR_BENCHMARK', {
  method: 'POST',
  path: '/sessions/:session_id/update_session_sector_benchmark',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.sector_alpha_settings = result.sector_alpha_settings;
  },
  failReducer(state, error) {
    notification.error({
      message: 'Could not save sector benchmark ETF',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const updateSessionDisplayMetrics = appManager.createApi<
  { session_id: number; display_metrics: number[] },
  unknown,
  AppState
>('UPDATE_DISPLAY_METRICS', {
  path: '/sessions/:session_id/update_display_metrics',
  method: 'POST',
  successReducer(state, result, payload) {
    // state.sessions.items[payload.session_id].display_metrics =
    //   payload.display_metrics;
  },
});

appManager.createSocketListener<number[], AppState>(
  'updated_display_metrics',
  (state, result) => {
    console.log('updated_display_metrics', result);
    const session = state.sessions.items[state.active_session];
    session.display_metrics = result;
  },
);

export const closePositions = appManager.createApi<
  { session_id: number },
  unknown,
  AppState
>('CLOSE_POSITIONS', {
  path: '/sessions/:session_id/close_positions',
  method: 'POST',
  successReducer() {
    //
  },
});

export const listOpenOrders = appManager.createApi<
  { session_id: number },
  {
    orders: types.AdminOpenOrder[];
  },
  AppState
>('LIST_OPEN_ORDERS', {
  path: '/sessions/:session_id/open_orders',
  method: 'GET',
  successReducer(state, result, payload) {
    const csvMaker = (orders: types.AdminOpenOrder[]) => {
      const headers: (keyof types.AdminOpenOrder)[] = [
        'session',
        'user',
        'underlying_asset_ric',
        'resourcetype',
        'transaction',
        'currency',
        'limit_price',
        'quantity',
        'timestamp',
      ];

      const csvRows = [];
      csvRows.push(headers.join(','));

      orders
        .map(order => {
          return headers
            .map(field => {
              switch (field) {
                case 'transaction':
                  return TRANSACTION_TYPES[order.transaction];
                default:
                  // MarkerOrder, MarketOnClose, MarketOnOpen don't have limit_price field
                  return order[field] ?? '';
              }
            })
            .join(',');
        })
        .forEach(row => {
          csvRows.push(row);
        });

      return csvRows.join('\n');
    };

    const downloadOrders = (data: string, id: number) => {
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `open_orders_${id}.csv`);
      a.click();
    };

    if (result.orders?.length) {
      const csvData = csvMaker(result.orders);
      downloadOrders(csvData, payload.session_id);
    }
  },
});

export const toggleSound = appManager.createApi<
  { allow_sound: boolean },
  { allow_sound: boolean; session_id: number },
  AppState
>('PATCH_SESSION_SOUND', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_sound',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.allow_sound = result.allow_sound;
  },
});

export const toggleInvestmentRationale = appManager.createApi<
  { require_investment_rationale: boolean },
  { require_investment_rationale: boolean; session_id: number },
  AppState
>('PATCH_REQUIRE_INVESTMENT_RATIONALE', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_require_investment_rationale',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.require_investment_rationale = result.require_investment_rationale;
  },
});

export const toggleSimplifiedOptionWriting = appManager.createApi<
  { allow_simplified_option_writing: boolean },
  { allow_simplified_option_writing: boolean; session_id: number },
  AppState
>('TOGGLE_SIMPLIFIED_OPTION_WRITING', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_simplified_option_writing',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.allow_simplified_option_writing =
      result.allow_simplified_option_writing;
  },
});

export const toggleAFSBondSwitchover = appManager.createApi<
  { session_id: number; afs_bond_switchover_enabled: boolean },
  { session_id: number; afs_bond_switchover_enabled: boolean },
  AppState
>('TOGGLE_AFS_BOND_SWITCHOVER', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_afs_bond_switchover',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.afs_bond_switchover_enabled = result.afs_bond_switchover_enabled;
  },
});

export const togglePayCoupons = appManager.createApi<
  { pay_coupons: boolean },
  { pay_coupons: boolean; session_id: number },
  AppState
>('TOGGLE_PAY_COUPONS', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_pay_coupons',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.pay_coupons = result.pay_coupons;
  },
});

export const toggleShowAnalysisTab = appManager.createApi<
  { session_id: number; show_analysis_tab: boolean },
  { show_analysis_tab: boolean; session_id: number },
  AppState
>('TOGGLE_SHOW_ANALYSIS_TAB', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_show_analysis_tab',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.show_analysis_tab = result.show_analysis_tab;
  },
});

export const toggleShowAnalysisStudioTab = appManager.createApi<
  { session_id: number; show_analysis_studio_tab: boolean },
  { show_analysis_studio_tab: boolean; session_id: number },
  AppState
>('TOGGLE_SHOW_ANALYSIS_STUDIO_TAB', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_show_analysis_studio_tab',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.show_analysis_studio_tab = result.show_analysis_studio_tab;
  },
});

export const toggleShowBiasAndReportInPlayerView = appManager.createApi<
  { session_id: number; show_bias_and_report_in_player_view: boolean },
  { show_bias_and_report_in_player_view: boolean; session_id: number },
  AppState
>('TOGGLE_SHOW_BIAS_AND_REPORT_IN_PLAYER_VIEW', {
  method: 'POST',
  path: '/sessions/:session_id/toggle_show_bias_and_report_in_player_view',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.show_bias_and_report_in_player_view =
      result.show_bias_and_report_in_player_view;
  },
});

export const updateNewsGroupingMode = appManager.createApi<
  {
    session_id: number;
    news_grouping_mode: 'all' | 'geography' | 'sector';
  },
  {
    session_id: number;
    news_grouping_mode: 'all' | 'geography' | 'sector';
  },
  AppState
>('UPDATE_NEWS_GROUPING_MODE', {
  method: 'POST',
  path: '/sessions/:session_id/update_news_grouping_mode',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (!session) {
      return;
    }
    session.news_grouping_mode = result.news_grouping_mode;

    const index = state.paginated_sessions.items.findIndex(
      s => s.id === result.session_id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].news_grouping_mode =
        result.news_grouping_mode;
    }
  },
});

export const updateSessionMarketsView = appManager.createApi<
  { session_id: number; which_markets: types.Session['which_markets'] },
  types.Session,
  AppState
>('UPDATE_SESSION_MARKETS_VIEW', {
  path: '/sessions/:session_id',
  method: 'PATCH',
  successReducer(state, result) {
    const session = state.sessions.items[result.id];
    if (session) session.which_markets = result.which_markets;
  },
  failReducer(_state, error) {
    notification.error({
      message: 'Could not update Markets view',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const updateSessionDv01Limits = appManager.createApi<
  {
    session_id: number;
    net_dv01_limit?: number | null;
    gross_dv01_limit?: number | null;
  },
  types.Session,
  AppState
>('UPDATE_SESSION_DV01_LIMITS', {
  path: '/sessions/:session_id',
  method: 'PATCH',
  successReducer(state, result) {
    const session = state.sessions.items[result.id];
    if (session) {
      session.net_dv01_limit = result.net_dv01_limit;
      session.gross_dv01_limit = result.gross_dv01_limit;
    }

    const index = state.paginated_sessions.items.findIndex(
      candidate => candidate.id === result.id,
    );
    if (index !== -1) {
      state.paginated_sessions.items[index].net_dv01_limit =
        result.net_dv01_limit;
      state.paginated_sessions.items[index].gross_dv01_limit =
        result.gross_dv01_limit;
    }
  },
  failReducer(_state, error) {
    notification.error({
      message: 'Could not update DV01 limits',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const updateMarketsV5HiddenAssets = appManager.createApi<
  { session_id: number; markets_v5_hidden_assets: number[] },
  { session_id: number; markets_v5_hidden_assets: number[] },
  AppState
>('UPDATE_MARKETS_V5_HIDDEN_ASSETS', {
  path: '/sessions/:session_id/update_markets_v5_hidden_assets',
  method: 'POST',
  successReducer(state, result) {
    const session = state.sessions.items[result.session_id];
    if (session) {
      session.markets_v5_hidden_assets = result.markets_v5_hidden_assets;
    }
  },
  failReducer(_state, error) {
    notification.error({
      message: 'Could not update Markets V5 player visibility',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

appManager.createSocketListener<
  { show_analysis_tab: boolean; session_id: number },
  AppState
>('toggle_show_analysis_tab', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.show_analysis_tab = result.show_analysis_tab;
});

appManager.createSocketListener<
  { show_analysis_studio_tab: boolean; session_id: number },
  AppState
>('toggle_show_analysis_studio_tab', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.show_analysis_studio_tab = result.show_analysis_studio_tab;
});

// Broadcast to the whole session group (players + trainer), so an already-open
// player Analysis Studio toggles its Bias & Final-report tabs instantly.
appManager.createSocketListener<
  { show_bias_and_report_in_player_view: boolean; session_id: number },
  AppState
>('toggle_show_bias_and_report_in_player_view', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.show_bias_and_report_in_player_view =
    result.show_bias_and_report_in_player_view;
});

appManager.createSocketListener<
  {
    session_id: number;
    news_grouping_mode: 'all' | 'geography' | 'sector';
  },
  AppState
>('news_grouping_mode_updated', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.news_grouping_mode = result.news_grouping_mode;

  const index = state.paginated_sessions.items.findIndex(
    s => s.id === result.session_id,
  );
  if (index !== -1) {
    state.paginated_sessions.items[index].news_grouping_mode =
      result.news_grouping_mode;
  }
});

appManager.createSocketListener<
  { session_id: number; markets_v5_hidden_assets: number[] },
  AppState
>('markets_v5_hidden_assets_updated', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) return;
  session.markets_v5_hidden_assets = result.markets_v5_hidden_assets;
});

appManager.createSocketListener<
  { allow_sound: boolean; session_id: number },
  AppState
>('toggle_sound', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.allow_sound = result.allow_sound;
});

appManager.createSocketListener<
  { require_investment_rationale: boolean; session_id: number },
  AppState
>('toggle_require_investment_rationale', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.require_investment_rationale = result.require_investment_rationale;
});

appManager.createSocketListener<
  { allow_simplified_option_writing: boolean; session_id: number },
  AppState
>('toggle_simplified_option_writing', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.allow_simplified_option_writing =
    result.allow_simplified_option_writing;
});

appManager.createSocketListener<
  { afs_bond_switchover_enabled: boolean; session_id: number },
  AppState
>('toggle_afs_bond_switchover', (state, result) => {
  const session = state.sessions.items[result.session_id];
  if (!session) {
    return;
  }
  session.afs_bond_switchover_enabled = result.afs_bond_switchover_enabled;
});

export const createUpdateGroupAllocations = appManager.createApi<
  { allocations: types.AssetGroupAllocationPayload[]; session_id?: number },
  types.AssetGroupAllocation[],
  AppState
>('CREATE_UPDATE_GROUP_ALLOCATIONS', {
  method: 'POST',
  path: '/sessions/:session_id/create_update_group_allocations',
  successReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    session.asset_group_allocations = result;
  },
});

export const updateMetricsLimits = appManager.createApi<
  { limits: types.MetricsLimitsNew[]; session_id?: number },
  types.MetricsLimitsNew[],
  AppState
>('UPDATE_METRICS_LIMITS', {
  method: 'POST',
  path: '/sessions/:session_id/update_metrics_limits',
  successReducer(state, result) {
    // response coming on metrics_limits_updated socket
  },
});

appManager.createSocketListener<MarketHoursType[], AppState>(
  'market_hours_updated',
  (state, result) => {
    // console.log('market_hours_updated: ', result);
    state.market_hours = makeAPIProp(result);
  },
);

export const updateMetricLimits = appManager.createApi<
  types.MetricLimits,
  unknown,
  AppState
>('UPDATE_METRIC_LIMITS', {
  method: 'POST',
  path: '/sessions/:session_id/update_metric_limits',
  successReducer(state, result) {
    //
  },
});

appManager.createSocketListener<types.MetricLimits, AppState>(
  'metric_limits_updated',
  (state, result) => {
    const session = state.sessions.items[state.active_session];
    session.metric_limit_low = result.low_limit;
    session.metric_limit_high = result.high_limit;
  },
);

appManager.createSocketListener<types.MetricsLimitsNew[], AppState>(
  'metrics_limits_updated',
  (state, result) => {
    const session = state.sessions.items[state.active_session];
    session.metrics_limits = result;
  },
);

export const fetchAutoTraderData = appManager.createApi<
  { session_id: number },
  // unknown,
  { players: number[]; auto_trade_until_date: string },
  AppState
>('FETCH_AUTO_TRADED_PLAYERS', {
  path: '/sessions/:session_id/fetch_auto_trader_data',
  method: 'GET',
  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].auto_traded_players =
      result.players;
    state.sessions.items[payload.session_id].auto_trade_until_date =
      result.auto_trade_until_date;
  },
});

export const updateAutoTradedPlayers = appManager.createApi<
  { session_id: number; player_ids: number[] },
  // unknown,
  number[],
  AppState
>('UPDATE_AUTO_TRADED_PLAYERS', {
  path: '/sessions/:session_id/update_auto_traded_players',
  method: 'POST',
  successReducer(state, result, payload) {
    state.sessions.items[payload.session_id].auto_traded_players = result;
    // state.underlying_assets.items = arrayToIndexedObject(result);
    // state.underlying_assets.fetched = true;
  },
});

export const updateAutoDealsPerPeriod = appManager.createApi<
  { session_id: number; deals_per_period: number },
  // unknown,
  number,
  AppState
>('UPDATE_AUTO_DEALS_PER_PERIOD', {
  path: '/sessions/:session_id/update_auto_deals_per_period',
  method: 'POST',
  successReducer(state, result, payload) {
    state.sessions.items[payload.session_id].auto_deals_per_period = result;
    // state.underlying_assets.items = arrayToIndexedObject(result);
    // state.underlying_assets.fetched = true;
  },
});

export const updateAutoTradeUntilDate = appManager.createApi<
  { session_id: number; date: moment.Moment | null },
  // unknown,
  string,
  AppState
>('UPDATE_AUTO_TRADED_UNTIL_DATE', {
  path: '/sessions/:session_id/update_auto_trade_until_date',
  method: 'POST',
  successReducer(state, result, payload) {
    console.log(result);
  },
});

export const startAutoTrade = appManager.createApi<
  { session_id: number },
  { auto_trader_status: number; status: 0 | 1 | 2 | 3 | 4 },
  // string,
  AppState
>('START_AUTO_TRADE', {
  path: '/sessions/:session_id/start_auto_trade',
  method: 'POST',
  successReducer(state, result, payload) {
    console.log(result);
    state.sessions.items[payload.session_id].auto_trader_status =
      result.auto_trader_status;
    state.sessions.items[payload.session_id].status = result.status;
  },
});

export const cloneSession = appManager.createApi<
  {
    session_id: number;
    target_klass_id: number;
  },
  {
    status: 'queued';
    task_id: string;
    source_session_id: number;
    target_klass_id: number;
  },
  AppState
>('CLONE_SESSION', {
  path: '/sessions/:session_id/clone_session',
  method: 'POST',
  startReducer(state) {
    // state.market
  },
  successReducer(state, result, payload) {
    notification.success({
      message: 'Deep clone queued',
      description: `Session ${result.source_session_id} will be cloned in the background (task ${result.task_id}).`,
      duration: 8,
    });
  },
  failReducer(state, error) {
    notification.error({
      message: 'Clone session failed',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const fetchLeaderboard = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  LeaderboardScoresType[],
  AppState
>('FETCH_LEADERBOARD', {
  path: '/sessions/:session_id/fetch_leaderboard',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    // state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    state.sessions.items[state.active_session].leaderboard_scores = result;
  },
});

export const fetchPnLDistributions = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  PnLDistributionAnalysisType[],
  AppState
>('FETCH_PNL_DISTRIBUTIONS', {
  path: '/sessions/:session_id/fetch_pnl_distributions',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[state.active_session].pnl_distributions = {
      waiting: true,
      data: [],
    };
    // state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    state.sessions.items[state.active_session].pnl_distributions.data = result;
    state.sessions.items[state.active_session].pnl_distributions.waiting =
      false;
  },
  failReducer(state, result, payload) {
    state.sessions.items[state.active_session].pnl_distributions.waiting =
      false;
  },
});

export const fetchTurnover = appManager.createApi<
  { session_id: number },
  PlayerTurnoverType[],
  AppState
>('FETCH_TURNOVER', {
  path: '/sessions/:session_id/fetch_turnover',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[state.active_session].turnover = {
      waiting: true,
      data: [],
    };
  },
  successReducer(state, result, payload) {
    state.sessions.items[state.active_session].turnover = {
      waiting: false,
      data: result,
    };
  },
  failReducer(state, result, payload) {
    const s = state.sessions.items[state.active_session];
    if (s.turnover) s.turnover.waiting = false;
  },
});

// Anonymised cohort return-vs-volatility scatter for the per-player (trainee)
// Analysis Studio. Identity-stripped server-side; empty below a min cohort size.
// Trainers don't use this — they build their own (named) scatter from the cohort
// they already hold.
export const fetchRiskScatter = appManager.createApi<
  { session_id: number },
  RiskScatterType,
  AppState
>('FETCH_RISK_SCATTER', {
  path: '/sessions/:session_id/fetch_risk_scatter',
  method: 'GET',
  startReducer(state, payload) {
    const s = state.sessions.items[state.active_session];
    s.risk_scatter = { waiting: true, data: s.risk_scatter?.data ?? null };
  },
  successReducer(state, result) {
    state.sessions.items[state.active_session].risk_scatter = {
      waiting: false,
      data: result,
    };
  },
  failReducer(state) {
    const s = state.sessions.items[state.active_session];
    if (s.risk_scatter) s.risk_scatter.waiting = false;
  },
});

// Per-asset price (mid) vs the player's signed positioning over the session step
// dates — feeds the Drivers "Positioning vs market" tile. One instrument at a
// time; the latest result replaces the previous one.
export const fetchAssetOverlay = appManager.createApi<
  { session_id: number; player_id: number; financial_instrument_id: number },
  AssetOverlayType,
  AppState
>('FETCH_ASSET_OVERLAY', {
  path: '/sessions/:session_id/fetch_asset_overlay',
  method: 'GET',
  startReducer(state, payload) {
    const s = state.sessions.items[state.active_session];
    s.asset_overlay = { waiting: true, data: s.asset_overlay?.data ?? null };
  },
  successReducer(state, result) {
    state.sessions.items[state.active_session].asset_overlay = {
      waiting: false,
      data: result,
    };
  },
  failReducer(state) {
    const s = state.sessions.items[state.active_session];
    if (s.asset_overlay) s.asset_overlay.waiting = false;
  },
});

export const fetchAnalysis = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  PlayerAnalysisType[],
  AppState
>('FETCH_ANALYSIS', {
  path: '/sessions/:session_id/fetch_analysis',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    state.sessions.items[payload.session_id].analysis = {
      data: [],
      waiting: true,
    };
  },
  successReducer(state, result, payload) {
    result.forEach(({ player_id, afs_dv01_history }) => {
      if (!afs_dv01_history) return;
      updateOrQueuePlayer(state, player_id, {
        afs_dv01_history: mergeAFSDV01History(
          currentPlayerData(state, player_id)?.afs_dv01_history,
          afs_dv01_history,
        ),
      });
      if (state.player.item.id === player_id) {
        state.player.item.afs_dv01_history = mergeAFSDV01History(
          state.player.item.afs_dv01_history,
          afs_dv01_history,
        );
      }
    });
    // The series belongs to each player. Keep the analysis slice focused on
    // analyzed metrics instead of retaining a second copy of every snapshot.
    const analysisData = result.map(analysis => {
      const withoutDv01History = { ...analysis };
      delete withoutDv01History.afs_dv01_history;
      return withoutDv01History;
    });
    state.sessions.items[state.active_session].analysis = {
      data: analysisData,
      waiting: false,
    };
  },
  failReducer(state, result, payload) {
    state.sessions.items[state.active_session].analysis = {
      data: [],
      waiting: false,
    };
  },
});

export const fetchConcentrations = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  ConcentrationType[],
  AppState
>('FETCH_CONCENTRATIONS', {
  path: '/sessions/:session_id/fetch_concentrations',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    state.sessions.items[payload.session_id].concentrations = {
      data: [],
      waiting: true,
    };
  },
  successReducer(state, result, payload) {
    state.sessions.items[state.active_session].concentrations = {
      data: result,
      waiting: false,
    };
  },
  failReducer(state, result, payload) {
    state.sessions.items[state.active_session].concentrations = {
      data: [],
      waiting: false,
    };
  },
});

export const fetchPrompts = appManager.createApi<
  { session_id?: number; type?: PromptKind },
  PromptType[],
  AppState
>('FETCH_PROMPTS', {
  path: '/prompts/fetch_prompts',
  method: 'GET',
  startReducer(state) {
    state.prompts.waiting = true;
  },
  successReducer(state, result) {
    state.prompts.items = arrayToIndexedObject(result);
    state.prompts.waiting = false;
  },
  failReducer(state) {
    state.prompts.waiting = false;
  },
});

export const fetchGroupPrompts = appManager.createApi<
  { session_id?: number; type?: PromptKind },
  PromptType[],
  AppState
>('FETCH_GROUP_PROMPTS', {
  path: '/prompts/fetch_prompts',
  method: 'GET',
  startReducer(state) {
    state.group_prompts.waiting = true;
  },
  successReducer(state, result) {
    state.group_prompts.items = arrayToIndexedObject(result);
    state.group_prompts.waiting = false;
  },
  failReducer(state) {
    state.group_prompts.waiting = false;
  },
});

export const fetchResponses = appManager.createApi<
  { session_id: number },
  LLMResponseType[],
  AppState
>('FETCH_LLM_RESPONSES', {
  path: '/prompts/fetch_llm_responses',
  method: 'POST',
  startReducer(state, payload) {
    state.llm_responses.waiting = true;
  },
  successReducer(state, result, payload) {
    state.llm_responses.items = arrayToIndexedObject(result);
    state.llm_responses.waiting = false;
  },
  failReducer(state, result, payload) {
    state.llm_responses.waiting = false;
  },
});

export const fetchGroupResponses = appManager.createApi<
  { session_id: number },
  LLMGroupResponseType[],
  AppState
>('FETCH_LLM_GROUP_RESPONSES', {
  path: '/prompts/fetch_llm_group_responses',
  method: 'POST',
  startReducer(state) {
    state.llm_group_responses.waiting = true;
  },
  successReducer(state, result) {
    state.llm_group_responses.items = arrayToIndexedObject(result);
    state.llm_group_responses.waiting = false;
  },
  failReducer(state) {
    state.llm_group_responses.waiting = false;
  },
});

export const createNewPrompt = appManager.createApi<
  { name: string; text?: string; type?: PromptKind },
  PromptType,
  AppState
>('CREATE_NEW_PROMPT', {
  method: 'POST',
  path: '/prompts/create_new_prompt',
  startReducer(state, props) {
    setPromptStoreWaiting(state, props.type || 'individual', true);
  },
  successReducer(state, result) {
    upsertPromptByType(state, result);
    setPromptStoreWaiting(state, result.type, false);
  },
  failReducer(state, error, props) {
    setPromptStoreWaiting(state, props.type || 'individual', false);
  },
});

export const savePrompt = appManager.createApi<
  { prompt_id: number; text: string },
  string,
  AppState
>('SAVE_PROMPT', {
  method: 'POST',
  path: '/prompts/save_prompt',
  startReducer(state) {
    state.prompts.waiting = true;
    state.group_prompts.waiting = true;
  },
  successReducer(state, result, payload) {
    updatePromptTextInStores(state, payload.prompt_id, result);
    state.prompts.waiting = false;
    state.group_prompts.waiting = false;
  },
  failReducer(state) {
    state.prompts.waiting = false;
    state.group_prompts.waiting = false;
  },
});

export const deletePrompt = appManager.createApi<
  { prompt_id: number; type?: PromptKind },
  {
    deleted_individual_response_ids: number[];
    deleted_group_response_ids: number[];
  },
  AppState
>('DELETE_PROMPT', {
  method: 'POST',
  path: '/prompts/delete_prompt',
  startReducer(state, props) {
    setPromptStoreWaiting(state, props.type || 'individual', true);
  },
  successReducer(state, result, payload) {
    result.deleted_individual_response_ids.forEach(
      id => delete state.llm_responses.items[id],
    );
    result.deleted_group_response_ids.forEach(
      id => delete state.llm_group_responses.items[id],
    );
    delete state.prompts.items[payload.prompt_id];
    delete state.group_prompts.items[payload.prompt_id];
    setPromptStoreWaiting(state, payload.type || 'individual', false);
  },
  failReducer(state, error, props) {
    setPromptStoreWaiting(state, props.type || 'individual', false);
  },
});

export const generateLLMresponse = appManager.createApi<
  {
    prompt_text: string;
    session_id: number;
    // One OR several players in a single request → ONE batch task.
    player_ids: number[];
    prompt_id: number;
    provider: string;
  },
  { new_responses: LLMResponseType[]; deleted_ids: number[] },
  AppState
>('GENERATE_LLM_RESPONSE', {
  method: 'POST',
  path: '/prompts/generate_llm_response',
  startReducer(state, props) {
    state.llm_responses.waiting = true;
    // Optimistic lock; cleared by report_generation_finished (or failReducer).
    state.llm_responses.generating = true;
  },
  successReducer(state, result) {
    // these responses only contain a placeholder until the llm finishes; the
    // full text streams in per player on the llm_response_generated socket.
    result.deleted_ids.forEach(id => {
      delete state.llm_responses.items[id];
    });
    result.new_responses.forEach(r => {
      state.llm_responses.items[r.id] = r;
    });
    // generating stays true — the batch task is running; report_generation_finished clears it.
  },
  failReducer(state, props) {
    state.llm_responses.waiting = false;
    state.llm_responses.generating = false; // no task was queued
  },
});

export const generateGroupLLMresponse = appManager.createApi<
  {
    prompt_text: string;
    prompt_id: number;
    session_id: number;
    player_ids: number[];
    group_report_name: string;
    provider: string;
  },
  { new_response: LLMGroupResponseType; deleted_id: number | null },
  AppState
>('GENERATE_GROUP_LLM_RESPONSE', {
  method: 'POST',
  path: '/prompts/generate_llm_response',
  startReducer(state) {
    state.llm_group_responses.waiting = true;
  },
  successReducer(state, result) {
    if (result.deleted_id !== null) {
      delete state.llm_group_responses.items[result.deleted_id];
    }
    state.llm_group_responses.items[result.new_response.id] =
      result.new_response;
  },
  failReducer(state) {
    state.llm_group_responses.waiting = false;
  },
});

export const deleteLLMresponse = appManager.createApi<
  { prompt_id: number; player_id: number },
  { id: number; prompt: number; player: number; session_id: number },
  AppState
>('DELETE_LLM_RESPONSE', {
  method: 'POST',
  path: '/prompts/delete_llm_response',
  startReducer(state, props) {
    state.llm_responses.waiting = true;
  },
  successReducer(state, result) {
    delete state.llm_responses.items[result.id];
    state.llm_responses.waiting = false;
  },
  failReducer(state, props) {
    state.llm_responses.waiting = false;
  },
});

export const deleteGroupLLMresponse = appManager.createApi<
  { id: number },
  { id: number; prompt: number; session: number; group_report_name: string },
  AppState
>('DELETE_GROUP_LLM_RESPONSE', {
  method: 'POST',
  path: '/prompts/delete_llm_group_response',
  startReducer(state) {
    state.llm_group_responses.waiting = true;
  },
  successReducer(state, result) {
    delete state.llm_group_responses.items[result.id];
    state.llm_group_responses.waiting = false;
  },
  failReducer(state) {
    state.llm_group_responses.waiting = false;
  },
});

appManager.createSocketListener<LLMResponseType, AppState>(
  'llm_response_generated',
  (state, result) => {
    removeOtherLLMResponsesForPair(state, result);
    state.llm_responses.items[result.id] = result;
    state.llm_responses.waiting = false;
  },
);

appManager.createSocketListener<
  { id: number; prompt: number; player: number; session_id: number },
  AppState
>('llm_response_deleted', (state, result) => {
  delete state.llm_responses.items[result.id];
  state.llm_responses.waiting = false;
});

appManager.createSocketListener<LLMGroupResponseType, AppState>(
  'llm_group_response_generated',
  (state, result) => {
    removeOtherLLMGroupResponsesForIdentity(state, result);
    state.llm_group_responses.items[result.id] = result;
    state.llm_group_responses.waiting = false;
  },
);

appManager.createSocketListener<
  { id: number; prompt: number; session: number; group_report_name: string },
  AppState
>('llm_group_response_deleted', (state, result) => {
  delete state.llm_group_responses.items[result.id];
  state.llm_group_responses.waiting = false;
});

/* ---- Bias analysis (LLM-scored behavioral biases) ---------------------- */

export const fetchBiasReports = appManager.createApi<
  { session_id: number },
  BiasReportType[],
  AppState
>('FETCH_BIAS_REPORTS', {
  path: '/prompts/fetch_bias_reports',
  method: 'POST',
  startReducer(state) {
    state.bias_reports.waiting = true;
  },
  successReducer(state, result) {
    state.bias_reports.items = arrayToIndexedObject(result);
    state.bias_reports.waiting = false;
  },
  failReducer(state) {
    state.bias_reports.waiting = false;
  },
});

export const generateSessionBiasReports = appManager.createApi<
  { session_id: number; provider: string; player_ids?: number[] },
  { queued: number; session_id: number },
  AppState
>('GENERATE_SESSION_BIAS_REPORTS', {
  method: 'POST',
  path: '/prompts/generate_session_bias_reports',
  startReducer(state) {
    state.bias_reports.waiting = true;
    // Optimistic lock: covers the click → task-start gap. The server's
    // bias_generation_started reinforces it; bias_generation_finished clears it.
    state.bias_reports.generating = true;
  },
  successReducer(state, result) {
    state.bias_reports.waiting = false;
    // generating stays true — the batch task is now running; the
    // bias_generation_finished websocket clears it.
    notification.info({
      message: `Bias analysis queued for ${result.queued} player(s). Results stream in as they complete.`,
    });
  },
  failReducer(state, error) {
    state.bias_reports.waiting = false;
    state.bias_reports.generating = false; // no task was queued
    notification.error({
      message: 'Bias analysis failed to start',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

appManager.createSocketListener<BiasReportType, AppState>(
  'bias_report_generated',
  (state, result) => {
    // One bias report per player — drop any superseded report for this player.
    Object.values(state.bias_reports.items).forEach(existing => {
      if (existing.id !== result.id && existing.player === result.player) {
        delete state.bias_reports.items[existing.id];
      }
    });
    state.bias_reports.items[result.id] = result;
    state.bias_reports.waiting = false;
  },
);

appManager.createSocketListener<
  { id: number; player: number; session_id: number; status: number },
  AppState
>('bias_report_error', (state, result) => {
  const existing = state.bias_reports.items[result.id];
  if (existing) {
    existing.status = result.status;
  }
  state.bias_reports.waiting = false;
});

/* ---- Batch generate-button lock (start/finish bracket per admin request) -
   The one batch Celery task emits *_generation_started at the start and
   *_generation_finished in a finally, so `generating` always clears even if the
   run fails. Per-player results still stream via the *_generated events above. */
appManager.createSocketListener<
  { session_id: number; player_ids?: number[] },
  AppState
>('bias_generation_started', state => {
  state.bias_reports.generating = true;
});

appManager.createSocketListener<{ session_id: number }, AppState>(
  'bias_generation_finished',
  state => {
    state.bias_reports.generating = false;
  },
);

appManager.createSocketListener<
  { session_id: number; player_ids?: number[] },
  AppState
>('report_generation_started', state => {
  state.llm_responses.generating = true;
});

appManager.createSocketListener<{ session_id: number }, AppState>(
  'report_generation_finished',
  state => {
    state.llm_responses.generating = false;
  },
);

/* ---- Market-regime segmentation (session-level, admin) ----------------- */

export const generateSessionMarketRegimes = appManager.createApi<
  { session_id: number; provider: string },
  { queued: number; session_id: number },
  AppState
>('GENERATE_SESSION_MARKET_REGIMES', {
  method: 'POST',
  path: '/prompts/generate_session_market_regimes',
  startReducer(state) {
    state.market_regimes.waiting = true;
  },
  successReducer() {
    notification.info({
      message:
        'Market-regime analysis queued. Results stream in when complete.',
    });
  },
  failReducer(state, error) {
    state.market_regimes.waiting = false;
    notification.error({
      message: 'Market-regime analysis failed to start',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const fetchRegimeOverlay = appManager.createApi<
  { session_id: number },
  RegimeOverlayType,
  AppState
>('FETCH_REGIME_OVERLAY', {
  method: 'GET',
  path: '/sessions/:session_id/fetch_regime_overlay',
  startReducer(state) {
    state.market_regimes.fetching = true;
  },
  successReducer(state, result) {
    state.market_regimes.overlay = result;
    state.market_regimes.fetching = false;
  },
  failReducer(state) {
    state.market_regimes.fetching = false;
  },
});

export const fetchMarketRegimes = appManager.createApi<
  { session_id: number },
  MarketRegimeReportType | null,
  AppState
>('FETCH_MARKET_REGIMES', {
  method: 'POST',
  path: '/prompts/fetch_market_regimes',
  successReducer(state, result) {
    const session = state.sessions.items[state.active_session];
    if (session) {
      session.market_regimes = result;
    }
  },
});

appManager.createSocketListener<MarketRegimeReportType, AppState>(
  'market_regimes_generated',
  (state, result) => {
    state.market_regimes.waiting = false;
    // Live-refresh the trainer studio's segments if that session is loaded.
    const session = result && state.sessions.items?.[result.session];
    if (session) {
      session.market_regimes = result;
    }
    notification.success({ message: 'Market regimes computed.' });
  },
);

appManager.createSocketListener<
  { id: number; session_id: number; status: number },
  AppState
>('market_regimes_error', state => {
  state.market_regimes.waiting = false;
  notification.error({ message: 'Market-regime analysis failed.' });
});

export const setPromptTextLocalEvent = appManager.createLocalEvent(
  'set_prompt_text',
  (state, payload) => {
    console.log('setting text in state');
    updatePromptTextInStores(state, payload.prompt_id, payload.text);
  },
);

export const recomputeLeaderboard = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  // LeaderboardScoresType[],
  // LeaderboardScoresType[],
  LeaderboardScoresType[],
  // unknown,
  AppState
>('RECOMPUTE_LEADERBOARD', {
  path: '/sessions/:session_id/recompute_leaderboard',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    // state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    console.log(result);
    // result.forEach(pla);
    state.sessions.items[state.active_session].leaderboard_scores = result;
  },
});

// Benchmark re-adjustment: non-destructive dry-run comparison (current vs proposed metrics for a
// candidate or the saved benchmark). Writes nothing server-side.
export const previewBenchmarkRecompute = appManager.createApi<
  {
    session_id: number;
    candidate_benchmark?: { fund_or_asset_ric: string; weight: number }[];
  },
  types.BenchmarkRecomputeComparison,
  AppState
>('PREVIEW_BENCHMARK_RECOMPUTE', {
  path: '/sessions/:session_id/preview_benchmark_recompute',
  method: 'POST',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_recompute_preview_waiting = true;
  },
  successReducer(state, result, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_recompute_preview_waiting = false;
    session.benchmark_recompute_preview = result;
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_recompute_preview_waiting = false;
  },
});

// Commit: persist the candidate benchmark (if given) + recompute the metrics. Async; the waiting
// flag is cleared by the benchmark_metrics_recompute_completed/failed admin socket events.
export const commitBenchmarkRecompute = appManager.createApi<
  {
    session_id: number;
    candidate_benchmark?: { fund_or_asset_ric: string; weight: number }[];
  },
  unknown,
  AppState
>('COMMIT_BENCHMARK_RECOMPUTE', {
  path: '/sessions/:session_id/commit_benchmark_recompute',
  method: 'POST',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_metrics_recompute_waiting = true;
  },
  successReducer() {
    // 202 accepted; the task acks completion over the admin socket
    // (benchmark_metrics_recompute_completed/failed), which clears the waiting flag.
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.benchmark_metrics_recompute_waiting = false;
  },
});

export const recomputeAnalysis = appManager.createApi<
  { session_id: number },
  // LeaderBoardType[],
  // LeaderboardScoresType[],
  // LeaderboardScoresType[],
  LeaderboardScoresType[],
  // unknown,
  AppState
>('RECOMPUTE_ANALYSIS', {
  path: '/sessions/:session_id/recompute_analysis',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.analysis_recompute_waiting = true;
    // The old Analysis tab still binds its table to analysis.waiting (cleared by
    // the finished_computing_analysis socket); keep that behaviour for trainers.
    session.analysis = {
      waiting: true,
      data: [],
    };
  },
  successReducer(state, result, payload) {
    // A 200 only means the task was queued — NOT that it finished. Keep
    // analysis_recompute_waiting set so the admin button stays disabled; it is
    // cleared when the worker emits 'analysis_recompute_completed' on the admin
    // channel (see state/app/admin/actions.ts).
    notification.info({
      message:
        'Analysis & concentrations recompute started. Results appear in the trainer’s Analysis Studio when ready.',
    });
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (session) {
      session.analysis_recompute_waiting = false;
    }
    notification.error({
      message: 'Analysis recompute failed',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

export const recomputePnLDistributions = appManager.createApi<
  { session_id: number },
  PnLDistributionAnalysisType[],
  // unknown,
  AppState
>('RECOMPUTE_PNL_DISTRIBUTIONS', {
  path: '/sessions/:session_id/recompute_pnl_distributions',
  method: 'GET',
  startReducer(state, payload) {
    const session = state.sessions.items[payload.session_id];
    if (!session) {
      return;
    }
    session.pnl_recompute_waiting = true;
    // The old Analysis tab still binds its table to pnl_distributions.waiting
    // (cleared by the computed_pnl_distributions socket); keep that for trainers,
    // but key it off payload.session_id so it is safe to dispatch from admin too.
    if (session.pnl_distributions) {
      session.pnl_distributions.waiting = true;
    }
  },
  successReducer(state, result, payload) {
    // A 200 only means the task was queued — NOT that it finished. Keep
    // pnl_recompute_waiting set so the admin button stays disabled; it is cleared
    // when the worker emits 'pnl_recompute_completed' on the admin channel (see
    // state/app/admin/actions.ts).
    notification.info({
      message:
        'PnL distributions recompute started. Results appear in the trainer’s Analysis Studio when ready.',
    });
  },
  failReducer(state, error, payload) {
    const session = state.sessions.items[payload.session_id];
    if (session) {
      session.pnl_recompute_waiting = false;
    }
    notification.error({
      message: 'PnL distributions recompute failed',
      description: normalizeApiError(error),
      duration: 0,
    });
  },
});

appManager.createSocketListener<
  {
    session_id: number;
    data: PlayerAnalysisType[];
    err: string;
    analysis_computed_at?: string | null;
    analysis_computed_for_datetime?: string | null;
  },
  AppState
>('finished_computing_analysis', (state, result) => {
  if (state.player.item.id === undefined) {
    // only update these if this is the trainer receiving
    console.log(result);
    if (result.err !== '') {
      console.log(result.err);
    }
    state.sessions.items[state.active_session].analysis.waiting = false;
    state.sessions.items[state.active_session].analysis.data = result.data;
    // Live-update the studio TopBar "last computed" line without a session refetch.
    state.sessions.items[state.active_session].analysis_computed_at =
      result.analysis_computed_at;
    state.sessions.items[state.active_session].analysis_computed_for_datetime =
      result.analysis_computed_for_datetime;
  }
});

appManager.createSocketListener<
  { data: PnLDistributionAnalysisType[]; err: string },
  AppState
>('computed_pnl_distributions', (state, result) => {
  if (state.player.item.id === undefined) {
    console.log(result);
    if (result.err !== '') {
      console.log(result.err);
    }
    state.sessions.items[state.active_session].pnl_distributions.waiting =
      false;
    state.sessions.items[state.active_session].pnl_distributions.data =
      result.data;
  }
});

appManager.createSocketListener<ConcentrationType[], AppState>(
  'computed_concentrations',
  (state, result) => {
    if (state.player.item.id === undefined) {
      console.log(result);
      state.sessions.items[state.active_session].concentrations.waiting = false;
      state.sessions.items[state.active_session].concentrations.data = result;
    }
  },
);

export const fetchBiases = appManager.createApi<
  { session_id: number },
  BiasesType[],
  AppState
>('FETCH_BIASES', {
  path: '/sessions/:session_id/fetch_biases',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    // state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    state.sessions.items[state.active_session].biases = result;
  },
});

export const updateInvestmentProfile = appManager.createApi<
  { session_id: number; investment_profile: number },
  types.AssetGroupAllocation[],
  AppState
>('UPDATE_INVESTMENT_PROFILE', {
  path: '/sessions/:session_id/update_investment_profile',
  method: 'POST',
  successReducer(state, result, payload) {
    // state.investment_profiles.items = arrayToIndexedObject(result);
    state.sessions.items[state.active_session].asset_group_allocations = result;
    state.sessions.items[state.active_session].investment_profile =
      payload.investment_profile;
  },
});

export const fetchPlayersSAAdecisions = appManager.createApi<
  { session_id: number },
  PlayerAllocationsByInvestmentProfile[],
  AppState
>('FETCH_PLAYERS_SAA_DECISIONS', {
  path: '/sessions/:session_id/fetch_players_saa_decisions',
  method: 'GET',
  startReducer(state, payload) {
    // state.sessions.items[payload.session_id].metrics_fetching = true;
    // state.sessions.items[payload.session_id].metrics_fetched = true;
  },
  successReducer(state, result, payload) {
    // console.log('FETCH_PLAYERS_SAA_DECISIONS');
    // console.log(result);
    // state.players.items = Object.fromEntries(
    //   Object.entries(state.players.items)
    //     .sort(([, a], [, b]) => b.score - a.score) // Sort players by score
    // );
    Object.values(state.players.items).forEach(player => {
      // console.log(player.name, player.score);
      const allocs = result.filter(res => res.player === player.id);
      player.strategic_group_allocations = allocs;
    });
  },
});

appManager.createSocketListener<boolean, AppState>(
  'currently_passing_time',
  (state, result) => {
    // console.log(result);
    // console.log('decreased delay');
    state.sessions.items[state.active_session].currently_passing_time = result;
    if (state.player.item.id !== undefined) {
      // player state, clear all pending rfq requests / responses
      state.player.item.sent_rfq_requests = [];
      state.player.item.rfq_responses = [];
    }
  },
);

export const fetchPlayersClientDealingStats = appManager.createApi<
  { session_id: number },
  {
    player_id: number;
    interbank_amount: number;
    client_interest: number;
    paid_lombard_interest: number;
    paid_repo_interest: number;
    interbank_interest: number;
    loans: number;
    depos: number;
  }[],
  AppState
>('FETCH_CLIENT_DEALING_STATS', {
  path: '/sessions/:session_id/fetch_players_client_dealing_stats',
  method: 'GET',
  startReducer(state, payload) {
    state.sessions.items[payload.session_id].fetching_client_dealing_stats =
      true;
    state.sessions.items[payload.session_id].fetched_client_dealing_stats =
      false;
  },
  successReducer(state, result, payload) {
    // Object.values(state.players.items).forEach(player => {
    //   const allocs = result.filter(res => res.player === player.id);
    //   player.strategic_group_allocations = allocs;
    // });
    // console.log(result);
    Object.values(result).forEach(res => {
      console.log(res);
      const player = state.players.items[res.player_id];
      if (!player) return; // guard if you ever get back an unknown id
      player.interbank_amount = res.interbank_amount;
      player.client_interest = res.client_interest;
      player.paid_lombard_interest = res.paid_lombard_interest;
      player.paid_repo_interest = res.paid_repo_interest;
      player.interbank_interest = res.interbank_interest;
      player.loans = res.loans;
      player.depos = res.depos;
    });
    state.sessions.items[payload.session_id].fetching_client_dealing_stats =
      false;
    state.sessions.items[payload.session_id].fetched_client_dealing_stats =
      true;
  },
  failReducer(state, error, payload) {
    state.sessions.items[payload.session_id].fetching_client_dealing_stats =
      false;
    state.sessions.items[payload.session_id].fetched_client_dealing_stats =
      false;
  },
});

appManager.createSocketListener<{ session_id: number }, AppState>(
  "live_bond_lifecycle_changed", (state, result) => {
    const session = state.sessions.items[result.session_id];
    if (session) session.live_bond_revision = (session.live_bond_revision ?? 0) + 1;
  },
);
